import { errorToJson, useGetRequest } from '$shared/utils';
import {
    ReservationStoresViewModel,
    StoreStoresReservationsDetailParams,
} from '~/lib/data-contract';
import { useNotification } from '~/shared/hooks/useNotification';
import { useStorePaths } from './useStorePaths';
import { StoresSearchParams } from './useStores';

type StoresWithStockSlugs = {
    productColorId?: string;
    variantId?: string;
};

type ReserveStoresSearchParams = Pick<
    StoreStoresReservationsDetailParams,
    'Latitude' | 'Longitude' | 'ZipCode' | 'City' | 'Radius'
>;

const defaultSearchParams: ReserveStoresSearchParams = {
    Latitude: undefined,
    Longitude: undefined,
    ZipCode: undefined,
    City: undefined,
    Radius: 50,
};

export type UseStoresWithStockProps = {
    /**
     * Product color and variantId. This is needed to find the specific product
     */
    slugs?: StoresWithStockSlugs;
    /**
     * Parameters that the search endpoint accepts
     */
    searchParams?: StoresSearchParams;
    /**
     * SearchTerm that will work for either city or zip. Will overwrite searchParams if both are provided
     */
    searchTerm?: string;
};

const isNumberRegex = /^\d+$/;

/**
 * Exposes store data
 */
export const useStoresWithStock = ({
    slugs = {},
    searchParams,
    searchTerm,
}: UseStoresWithStockProps) => {
    const { reservationsPath } = useStorePaths();
    const { push } = useNotification();

    const params: ReserveStoresSearchParams = {
        ...defaultSearchParams,
        ...searchParams,
        ...(searchTerm && { [isNumberRegex.test(searchTerm) ? 'ZipCode' : 'City']: searchTerm }),
    };

    const { Latitude, Longitude, City, ZipCode } = params;
    const { productColorId, variantId } = slugs;

    const hasSlugs = Boolean(productColorId && variantId);
    const isCoordsValid = Boolean(Latitude && Longitude);
    const hasMandatoryParams = Boolean(isCoordsValid || City || ZipCode);
    const isCityValid = (City?.length || 0) > 2;
    const isZipCodeValid = (ZipCode?.length || 0) === 4 || ZipCode === '0';
    const isTermValid = isCityValid || isZipCodeValid;
    const shouldSearch = hasSlugs && hasMandatoryParams && isTermValid;

    const { data, error, isLoading, isError, status, isFetching, isFetched } = useGetRequest<
        ReservationStoresViewModel,
        Response
    >(`${reservationsPath}/${productColorId}/${variantId}`, {
        enabled: shouldSearch,
        keepPreviousData: hasMandatoryParams,
        params,
        retry: 1,
        onError: async (error: Response) => {
            const { level, errorMessages } = await errorToJson(error);
            push({ severity: level, text: errorMessages });
        },
    });

    return {
        data: data || {},
        status,
        error,
        isLoading,
        isError,
        isFetching,
        isFetched,
    };
};
